import { workItemFieldLabel } from '@orbind/core'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { api } from '../../lib/api.ts'
import type { Bootstrap } from '../../lib/catalog.ts'
import { useT } from '../../lib/i18n.tsx'
import { downloadFile, uploadWorkItemFile, useWiMutation, type WorkItemDetail } from './api.ts'
import { EmptyBlock, StatusPill, fileIcon, formatBytes, initialsOf, timeAgo, useConfirm } from './bits.tsx'
import { useQueryClient } from '@tanstack/react-query'

/** Secoes Relacoes, Criterios, Comentarios, Arquivos e Historico (fatias 05/06). */

export function RelationsSection({ detail, onOpenItem }: { detail: WorkItemDetail; onOpenItem: (id: string) => void }) {
  const t = useT()
  const confirm = useConfirm()
  const relations = detail.relations.filter((r) => r.relationTypeCode !== 'ACCEPTANCE_CRITERION_OF')
  const remove = useWiMutation(detail.item.id, async (relationId: string) =>
    api['work-items'][':id'].relations[':relationId'].$delete({ param: { id: detail.item.id, relationId } }),
  )

  return (
    <div>
      <p style={{ fontFamily: 'var(--ob-font-body)', fontSize: 12.5, color: 'var(--ob-text-muted)', margin: '0 0 14px' }}>
        Hierarquia representa decomposição. Relação representa dependência, bloqueio ou vínculo contextual.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {relations.map((relation) => (
          <div key={relation.relationId} className="wi-rel__row">
            <button type="button" className="wi-rel__main" onClick={() => onOpenItem(relation.other.id)}>
              <span className="wi-rel__label">{t(relation.labelKey)}</span>
              <span className="wi-rel__code">{relation.other.code}</span>
              <span className="wi-rel__title">{relation.other.title}</span>
              <i className="fas fa-arrow-right" aria-hidden style={{ fontSize: 10, color: 'var(--ob-text-muted)' }} />
            </button>
            <button
              type="button"
              className="wi-rel__remove"
              aria-label="Remover relação"
              title="Remover relação"
              onClick={() =>
                confirm.ask('A relação entre os dois itens será removida.', () => remove.mutate(relation.relationId))
              }
            >
              <i className="fas fa-trash-alt" aria-hidden style={{ fontSize: 10 }} />
            </button>
          </div>
        ))}
      </div>
      {relations.length === 0 ? (
        <EmptyBlock
          icon="fas fa-link"
          title="Nenhuma relação registrada"
          text="Relacione este item a dependências, bloqueios ou itens duplicados para dar visibilidade ao fluxo."
        />
      ) : null}
      {confirm.element}
    </div>
  )
}

export function CriteriaSection({ detail, onOpenItem }: { detail: WorkItemDetail; onOpenItem: (id: string) => void }) {
  const t = useT()
  const confirm = useConfirm()
  const criteria = detail.relations.filter((r) => r.relationTypeCode === 'ACCEPTANCE_CRITERION_OF')
  const pendingIds = new Set(detail.completion.pendingRequiredCriteria.map((c) => c.id))
  const remove = useWiMutation(detail.item.id, async (relationId: string) =>
    api['work-items'][':id'].relations[':relationId'].$delete({ param: { id: detail.item.id, relationId } }),
  )

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {criteria.map((criterion) => {
          const pending = pendingIds.has(criterion.other.id)
          return (
            <div key={criterion.relationId} className="wi-rel__row">
              <button type="button" className="wi-rel__main" onClick={() => onOpenItem(criterion.other.id)}>
                <i
                  className={pending ? 'far fa-circle' : 'fas fa-check-circle'}
                  aria-hidden
                  style={{ fontSize: 13, color: pending ? 'var(--ob-warning)' : 'var(--ob-success)', flex: 'none' }}
                />
                <span className="wi-rel__code">{criterion.other.code}</span>
                <span className="wi-rel__title">{criterion.other.title}</span>
                <span
                  className="wi-rel__label"
                  style={
                    criterion.isRequired
                      ? { background: 'var(--ob-warning-surface)', color: 'var(--ob-warning-deep)' }
                      : { background: 'var(--ob-background-3)', color: 'var(--ob-text-subtle)' }
                  }
                >
                  {criterion.isRequired ? 'Obrigatório' : 'Opcional'}
                </span>
                <StatusPill tone={pending ? 'warning' : 'success'}>{pending ? 'Pendente' : 'Atendido'}</StatusPill>
              </button>
              <button
                type="button"
                className="wi-rel__remove"
                aria-label="Excluir critério"
                title="Excluir critério de aceite"
                onClick={() =>
                  confirm.ask('O vínculo de critério de aceite será removido.', () => remove.mutate(criterion.relationId))
                }
              >
                <i className="fas fa-trash-alt" aria-hidden style={{ fontSize: 10 }} />
              </button>
            </div>
          )
        })}
      </div>
      {criteria.length === 0 ? (
        <EmptyBlock
          icon="fas fa-clipboard-check"
          title="Nenhum critério de aceite definido"
          text="Critérios obrigatórios pendentes impedem a conclusão do item principal."
        />
      ) : null}
      {confirm.element}
    </div>
  )
}

/** Realça @menções (pelos nomes conhecidos dos membros) no texto do comentário. */
function textoComMencoes(texto: string, nomes: string[]): ReactNode {
  if (nomes.length === 0) return texto
  // ordena por tamanho para casar "Ana Maria" antes de "Ana"
  const ordenados = [...nomes].sort((a, b) => b.length - a.length)
  const escapar = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`@(${ordenados.map(escapar).join('|')})`, 'g')
  const out: ReactNode[] = []
  let ultimo = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(texto)) !== null) {
    if (m.index > ultimo) out.push(texto.slice(ultimo, m.index))
    out.push(
      <span key={m.index} className="wi-com__mention">
        {m[0]}
      </span>,
    )
    ultimo = m.index + m[0].length
  }
  if (ultimo < texto.length) out.push(texto.slice(ultimo))
  return out
}

export function CommentsSection({
  detail,
  members,
}: {
  detail: WorkItemDetail
  members: Bootstrap['members']
}) {
  const [text, setText] = useState('')
  // token @ ativo: posição do @ e o que foi digitado depois dele
  const [mencao, setMencao] = useState<{ ini: number; q: string } | null>(null)
  const [selIdx, setSelIdx] = useState(0)
  const taRef = useRef<HTMLTextAreaElement>(null)
  const workItemId = detail.item.id
  const byId = new Map(members.map((c) => [c.id, c]))
  const nomes = members.map((m) => m.name)

  // ao abrir a seção, o cursor já fica dentro do comentário
  useEffect(() => {
    taRef.current?.focus()
  }, [])

  const post = useWiMutation(workItemId, async (value: string) =>
    api['work-items'][':id'].comments.$post({ param: { id: workItemId }, json: { text: value } }),
  )

  // detecta o @ ativo a partir do texto e da posição do cursor
  const detectar = (valor: string, cursor: number) => {
    const antes = valor.slice(0, cursor)
    const m = antes.match(/(?:^|\s)@([^\s@]*)$/)
    if (!m) return null
    const q = m[1] ?? ''
    return { ini: cursor - q.length - 1, q }
  }

  const candidatos = mencao
    ? members.filter((m) => m.name.toLowerCase().includes(mencao.q.toLowerCase())).slice(0, 6)
    : []

  const aoDigitar = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value
    setText(v)
    setMencao(detectar(v, e.target.selectionStart ?? v.length))
    setSelIdx(0)
  }

  const inserir = (membro: { id: string; name: string }) => {
    if (!mencao) return
    const antes = text.slice(0, mencao.ini)
    const depois = text.slice(mencao.ini + 1 + mencao.q.length)
    const token = `@${membro.name} `
    setText(antes + token + depois)
    setMencao(null)
    const pos = (antes + token).length
    requestAnimationFrame(() => {
      const ta = taRef.current
      if (ta) {
        ta.focus()
        ta.setSelectionRange(pos, pos)
      }
    })
  }

  const enviar = () => {
    const value = text.trim()
    if (!value) return
    post.mutate(value)
    setText('')
    setMencao(null)
  }

  const aoTecla = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mencao && candidatos.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelIdx((i) => (i + 1) % candidatos.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelIdx((i) => (i - 1 + candidatos.length) % candidatos.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const alvo = candidatos[selIdx] ?? candidatos[0]
        if (alvo) inserir(alvo)
        return
      }
      if (e.key === 'Escape') {
        setMencao(null)
        return
      }
    }
    // Enter (sem shift) envia o comentário
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      enviar()
    }
  }

  return (
    <div>
      {detail.collab.comments.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 18 }}>
          {detail.collab.comments.map((comment) => {
            const author = byId.get(comment.createdBy)
            return (
              <div key={comment.id} className={`wi-com__row${comment.isUnread ? ' wi-com__row--unread' : ''}`}>
                <span className="wi-com__avatar">{initialsOf(author?.name ?? '?')}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, marginBottom: 4 }}>
                    <span className="wi-com__author">{author?.name ?? 'Membro'}</span>
                    <span className="wi-com__time">{timeAgo(comment.createdAt)}</span>
                    {comment.isUnread ? <span className="wi-com__new">Não lido</span> : null}
                  </div>
                  <p className="wi-com__text">{textoComMencoes(comment.text, nomes)}</p>
                </div>
              </div>
            )
          })}
        </div>
      ) : null}

      <div className="wi-com__composer" style={{ position: 'relative' }}>
        <textarea
          ref={taRef}
          className="wi-com__textarea"
          rows={2}
          placeholder="Escreva um comentário, use @ para mencionar alguém"
          value={text}
          onChange={aoDigitar}
          onKeyDown={aoTecla}
        />
        {/* lista de @menção: nomes dos membros, com busca pelo que foi digitado */}
        {mencao && candidatos.length > 0 ? (
          <div className="wi-com__mentions" role="listbox">
            {candidatos.map((m, i) => (
              <button
                key={m.id}
                type="button"
                role="option"
                aria-selected={i === selIdx}
                className={`wi-com__mentionopt${i === selIdx ? ' wi-com__mentionopt--on' : ''}`}
                onMouseEnter={() => setSelIdx(i)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  inserir(m)
                }}
              >
                <span className="wi-chip-member" style={{ width: 22, height: 22, fontSize: 9 }}>
                  {initialsOf(m.name)}
                </span>
                {m.name}
              </button>
            ))}
          </div>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
          <span style={{ flex: 1 }} />
          <button type="button" className="wi-com__post" onClick={enviar}>
            Comentar
          </button>
        </div>
      </div>
    </div>
  )
}

export function commentsHeaderExtras(detail: WorkItemDetail, markRead: () => void) {
  if (detail.collab.unreadCount === 0) return null
  return (
    <>
      <span style={{ flex: 1 }} />
      <button type="button" className="wi-com__markread" onClick={markRead}>
        <i className="fas fa-check-double" aria-hidden style={{ fontSize: 9 }} />
        Marcar {detail.collab.unreadCount} como lida{detail.collab.unreadCount > 1 ? 's' : ''}
      </button>
    </>
  )
}

export function FilesSection({ detail }: { detail: WorkItemDetail }) {
  const confirm = useConfirm()
  const queryClient = useQueryClient()
  const [dragOver, setDragOver] = useState(false)
  const workItemId = detail.item.id

  const remove = useWiMutation(workItemId, async (fileId: string) =>
    api['work-items'].files[':fileId'].$delete({ param: { fileId } }),
  )

  const upload = async (files: FileList | null) => {
    if (!files) return
    for (const file of Array.from(files)) {
      await uploadWorkItemFile(workItemId, file)
    }
    queryClient.invalidateQueries({ queryKey: ['work-item', workItemId] })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {detail.collab.files.length === 0 ? (
        <EmptyBlock
          icon="fas fa-folder-open"
          title="Nenhum arquivo anexado"
          text="Anexe evidências, especificações ou materiais de apoio a este Item de Trabalho."
        />
      ) : null}
      {detail.collab.files.map((file) => (
        <div key={file.id} className="wi-file__row">
          <span className="wi-file__icon">
            <i className={fileIcon(file.mimeType)} aria-hidden style={{ fontSize: 13 }} />
          </span>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="wi-file__name">{file.name}</span>
            <span className="wi-file__meta">
              {formatBytes(file.sizeBytes)} · {timeAgo(file.createdAt)}
            </span>
          </span>
          <button
            type="button"
            className="wi-file__btn"
            aria-label="Baixar"
            title="Baixar"
            onClick={() => downloadFile(file.id, file.name)}
          >
            <i className="fas fa-download" aria-hidden style={{ fontSize: 11 }} />
          </button>
          <button
            type="button"
            className="wi-file__btn wi-file__btn--danger"
            aria-label="Remover arquivo"
            title="Remover arquivo"
            onClick={() => confirm.ask(`O arquivo "${file.name}" será removido do item.`, () => remove.mutate(file.id))}
          >
            <i className="fas fa-trash-alt" aria-hidden style={{ fontSize: 11 }} />
          </button>
        </div>
      ))}
      <label
        className={`wi-file__drop${dragOver ? ' wi-file__drop--over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          void upload(e.dataTransfer.files)
        }}
      >
        <i className="fas fa-cloud-upload-alt" aria-hidden style={{ fontSize: 12 }} />
        Arraste arquivos ou clique para enviar
        <input type="file" multiple style={{ display: 'none' }} onChange={(e) => void upload(e.target.files)} />
      </label>
      {confirm.element}
    </div>
  )
}

type HistoryEntry = {
  id: string
  field: string
  previousValue: string | null
  newValue: string | null
  comment?: string | null
  source: string
  createdAt: string
  createdBy: string | null
  author?: { id: string; name: string; avatarUrl: string | null } | null
}

/**
 * Timeline do item: quem mudou o quê, de qual valor para qual, quando.
 * Os valores ja chegam legiveis do servidor; aqui so montamos a frase com o
 * rotulo pt-BR do campo (`workItemFieldLabel`).
 */
export function HistorySection({
  history,
  members,
}: {
  history: readonly HistoryEntry[]
  members: Bootstrap['members']
}) {
  const byId = new Map(members.map((c) => [c.id, c]))
  const authorOf = (entry: HistoryEntry) =>
    entry.author ?? (entry.createdBy ? (byId.get(entry.createdBy) ?? null) : null)

  return (
    <div>
      {history.map((entry) => {
        const author = authorOf(entry)
        const label = workItemFieldLabel(entry.field)
        const from = entry.previousValue ?? '—'
        const to = entry.newValue ?? '—'
        return (
          <div key={entry.id} className="wi-hist__row">
            {author?.avatarUrl ? (
              <img className="wi-hist__avatar" src={author.avatarUrl} alt="" />
            ) : (
              <span className="wi-hist__avatar wi-hist__avatar--initials">{author ? initialsOf(author.name) : 'SY'}</span>
            )}
            <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <span className="wi-hist__text">
                <strong>{author?.name ?? 'Sistema'}</strong>{' '}
                {entry.field === 'work_item_created' ? (
                  <>
                    criou o item <span className="wi-hist__val wi-hist__val--new">{entry.newValue}</span>
                  </>
                ) : entry.previousValue === null && entry.newValue !== null ? (
                  <>
                    definiu <strong>{label}</strong> como <span className="wi-hist__val wi-hist__val--new">{to}</span>
                  </>
                ) : entry.newValue === null && entry.previousValue !== null ? (
                  <>
                    limpou <strong>{label}</strong> (era <span className="wi-hist__val">{from}</span>)
                  </>
                ) : (
                  <>
                    alterou <strong>{label}</strong> de <span className="wi-hist__val">{from}</span> para{' '}
                    <span className="wi-hist__val wi-hist__val--new">{to}</span>
                  </>
                )}
              </span>
              {entry.comment ? <span className="wi-hist__comment">“{entry.comment}”</span> : null}
              <span className="wi-hist__meta">
                {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(entry.createdAt))}
                {entry.source !== 'MANUAL' ? ` · ${entry.source}` : ''}
              </span>
            </span>
          </div>
        )
      })}
      {history.length === 0 ? (
        <p style={{ fontFamily: 'var(--ob-font-body)', fontSize: 12.5, color: 'var(--ob-text-muted)' }}>Sem eventos ainda.</p>
      ) : null}
    </div>
  )
}

type MovEvent = {
  id: string
  field: string
  previousValue: string | null
  newValue: string | null
  comment: string | null
  source: string
  createdAt: string
  author: { id: string; name: string; avatarUrl: string | null } | null
}
type Movement = {
  id: string
  flowStepId: string | null
  stepName: string | null
  flowName: string | null
  enteredAt: string
  exitedAt: string | null
  source: string
  author: { id: string; name: string; avatarUrl: string | null } | null
  events: MovEvent[]
  sintetico?: boolean
}

const movDtf = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

/** Tempo que o item passou (ou está) numa etapa, em pt-BR compacto. */
function formatDuracao(fromISO: string, toISO: string | null): string {
  const from = new Date(fromISO).getTime()
  const to = toISO ? new Date(toISO).getTime() : Date.now()
  let s = Math.max(0, Math.round((to - from) / 1000))
  const d = Math.floor(s / 86400)
  s -= d * 86400
  const h = Math.floor(s / 3600)
  s -= h * 3600
  const m = Math.floor(s / 60)
  if (d > 0) return `${d} d${h > 0 ? ` ${h} h` : ''}`
  if (h > 0) return `${h} h${m > 0 ? ` ${m} min` : ''}`
  if (m > 0) return `${m} min`
  return '<1 min'
}

/** Frase pt-BR de um evento do histórico ocorrido dentro da etapa. */
function fraseEvento(ev: MovEvent) {
  const label = workItemFieldLabel(ev.field)
  const from = ev.previousValue ?? '—'
  const to = ev.newValue ?? '—'
  if (ev.field === 'work_item_created')
    return (
      <>
        criou o item <span className="wi-hist__val wi-hist__val--new">{to}</span>
      </>
    )
  if (ev.previousValue === null && ev.newValue !== null)
    return (
      <>
        definiu <strong>{label}</strong> como <span className="wi-hist__val wi-hist__val--new">{to}</span>
      </>
    )
  if (ev.newValue === null && ev.previousValue !== null)
    return (
      <>
        limpou <strong>{label}</strong> (era <span className="wi-hist__val">{from}</span>)
      </>
    )
  return (
    <>
      alterou <strong>{label}</strong> de <span className="wi-hist__val">{from}</span> para{' '}
      <span className="wi-hist__val wi-hist__val--new">{to}</span>
    </>
  )
}

/**
 * Movimentação: desenha o CAMINHO das etapas por onde o item passou. Cada nó
 * traz etapa, fluxo, quem moveu, data/hora e quanto tempo ficou; clicar abre os
 * eventos do histórico ocorridos enquanto o item estava naquela etapa.
 */
export function MovimentacaoSection({
  movements,
  loading,
}: {
  movements: readonly Movement[]
  loading: boolean
}) {
  const [aberto, setAberto] = useState<string | null>(null)

  if (loading && movements.length === 0)
    return <p style={{ fontFamily: 'var(--ob-font-body)', fontSize: 12.5, color: 'var(--ob-text-muted)' }}>Carregando…</p>
  if (movements.length === 0)
    return (
      <EmptyBlock
        icon="fas fa-route"
        title="Sem movimentação ainda"
        text="Quando o item mudar de etapa no fluxo, o caminho aparece aqui."
      />
    )

  return (
    <div className="wi-mov">
      {movements.map((mov, i) => {
        const atual = mov.exitedAt === null
        const expandido = aberto === mov.id
        const temEventos = mov.events.length > 0
        return (
          <div key={mov.id} className={`wi-mov__node${atual ? ' wi-mov__node--atual' : ''}`}>
            <div className="wi-mov__rail" aria-hidden>
              <span className="wi-mov__dot" />
              {i < movements.length - 1 ? <span className="wi-mov__line" /> : null}
            </div>
            <div className="wi-mov__card">
              <button
                type="button"
                className="wi-mov__head"
                disabled={!temEventos}
                onClick={() => setAberto(expandido ? null : mov.id)}
                title={temEventos ? 'Ver o que aconteceu nesta etapa' : 'Nenhum evento registrado nesta etapa'}
              >
                <span className="wi-mov__etapa">
                  <strong>{mov.stepName ?? 'Etapa'}</strong>
                  {mov.flowName ? <span className="wi-mov__fluxo">{mov.flowName}</span> : null}
                </span>
                {atual ? <span className="wi-mov__badge">Etapa atual</span> : null}
                <span className="wi-mov__dur" title="Tempo nesta etapa">
                  <i className="far fa-clock" aria-hidden /> {formatDuracao(mov.enteredAt, mov.exitedAt)}
                </span>
                {temEventos ? (
                  <span className="wi-mov__count">
                    {mov.events.length} <i className={`fas fa-chevron-${expandido ? 'up' : 'down'}`} aria-hidden />
                  </span>
                ) : null}
              </button>
              <div className="wi-mov__meta">
                <strong>{mov.author?.name ?? 'Sistema'}</strong> moveu · {movDtf.format(new Date(mov.enteredAt))}
              </div>
              {expandido && temEventos ? (
                <div className="wi-mov__events">
                  {mov.events.map((ev) => {
                    const a = ev.author ?? null
                    return (
                      <div key={ev.id} className="wi-mov__event">
                        {a?.avatarUrl ? (
                          <img className="wi-hist__avatar" src={a.avatarUrl} alt="" />
                        ) : (
                          <span className="wi-hist__avatar wi-hist__avatar--initials">{a ? initialsOf(a.name) : 'SY'}</span>
                        )}
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                          <span className="wi-hist__text">
                            <strong>{a?.name ?? 'Sistema'}</strong> {fraseEvento(ev)}
                          </span>
                          {ev.comment ? <span className="wi-hist__comment">“{ev.comment}”</span> : null}
                          <span className="wi-hist__meta">{movDtf.format(new Date(ev.createdAt))}</span>
                        </span>
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}
